import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogoWithDomainSwitcher } from '@/shared/components/layout/LogoWithDomainSwitcher';
import {
  Shield,
  ChevronRight,
  X,
  Database,
  RefreshCw,
  DollarSign,
  MessageSquare,
  Trello,
  FileText,
  Activity
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ForwardRefExoticComponent<any>;
  shortName?: string;
}

/**
 * AdminSidebar component - Navigation sidebar for admin domain
 * Responsive design with mobile support
 */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();

  // Navigation items for admin domain
  const navigationItems: NavigationItem[] = [
    {
      name: 'Permissions',
      href: '/admin/users',
      icon: Shield,
      shortName: 'Permissions'
    },
    {
      name: 'Data Import',
      href: '/admin/data-import',
      icon: Database,
      shortName: 'Data Import'
    },
    {
      name: 'Cycle Management',
      href: '/admin/cycles',
      icon: RefreshCw,
      shortName: 'Cycles'
    },
    {
      name: 'Spend Configuration',
      href: '/admin/spend-config',
      icon: DollarSign,
      shortName: 'Spend Config'
    },
    {
      name: 'Slack Integration',
      href: '/admin/slack-integration',
      icon: MessageSquare,
      shortName: 'Slack'
    },
    {
      name: 'Slack Templates',
      href: '/admin/slack-templates',
      icon: FileText,
      shortName: 'Templates'
    },
    {
      name: 'Asana Integration',
      href: '/admin/asana-integration',
      icon: Trello,
      shortName: 'Asana'
    },
    {
      name: 'Apollo Enrichment',
      href: '/admin/apollo-enrichment',
      icon: Database,
      shortName: 'Apollo'
    },
    {
      name: 'Function Health',
      href: '/admin/functions',
      icon: Activity,
      shortName: 'Functions'
    }
  ];

  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar - Floating Container */}
      <div
        className={`fixed z-50 w-72 bg-white transform transition-transform duration-300 ease-in-out
          lg:left-4 lg:top-4 lg:bottom-4 lg:rounded-2xl lg:shadow-2xl lg:translate-x-0
          inset-y-0 left-0 shadow-lg
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex flex-col h-full lg:rounded-2xl overflow-hidden">
          {/* Sidebar header - Mobile close button only */}
          <div className="flex items-center justify-end p-4 border-b border-gray-200 lg:hidden">
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Logo with integrated Domain Switcher */}
          <LogoWithDomainSwitcher />

          {/* Navigation */}
          <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    // Close mobile menu when navigating
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-purple-50 text-purple-700 border-r-2 border-purple-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  title={item.name} // Show full name on hover
                >
                  <Icon
                    className={`mr-3 h-5 w-5 transition-colors ${
                      isActive ? 'text-purple-500' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  <span className="flex-1">
                    {item.shortName || item.name}
                  </span>
                  {isActive && (
                    <ChevronRight className="h-4 w-4 text-purple-500" />
                  )}
                </NavLink>
              );
            })}
          </nav>

        </div>
      </div>
    </>
  );
};

export default Sidebar;